import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { LoadingSpinner } from '../components/Common';
import { useAuth } from '../context/useAuth';
import DashboardViewPage from './dashboard/DashboardViewPage';
import { Box, Typography, Button, Paper, Divider, Chip, Tabs, Tab } from '@mui/material';
import { ArrowLeft, LayoutDashboard, BrainCircuit } from 'lucide-react';
import { DynamicIcon } from '../components/icons';

const MLAppRunnerPage = ({ contentId: propContentId, onBack }) => {
    const params = useParams();
    const contentId = propContentId || params.contentId;
    const navigate = useNavigate();
    const { token } = useAuth();

    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(!!contentId);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!contentId) {
            setError("No Content ID provided. Cannot load preview.");
            return;
        }
    }, [contentId]);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                setLoading(true);
                const data = await apiFetch(`/contents/${contentId}`, token);
                if (!data) throw new Error("Content not found");
                setContent(data);
            } catch (err) {
                console.error("Failed to load content:", err);
                setError(err.message || "Failed to load content");
            } finally {
                setLoading(false);
            }
        };

        if (contentId) {
            fetchContent();
        }
    }, [contentId, token]);

    // --- UI/UX Replica of TenantAppRunnerPage ---
    // (Adapted for Preview Mode - lacking Instance context)

    const [tabIndex, setTabIndex] = useState(0);
    const contentVersion = content?.version || '1.0';
    const lastRunTime = 'N/A (Preview)';

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) return <LoadingSpinner />;

    if (error) {
        return (
            <Box p={4}>
                <Typography color="error" variant="h6">Error: {error}</Typography>
                <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
            </Box>
        );
    }

    if (!content) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default', overflow: 'hidden' }}>
            {/* Header - Matches TenantAppRunnerPage */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 shrink-0">
                <div className="max-w-7xl mx-auto flex justify-between items-start">
                    <div className="flex items-start gap-4">
                        <Button onClick={onBack} sx={{ minWidth: 'auto', p: 1, mr: 1, mt: 0.5 }} aria-label="Back" title="Back">
                            <ArrowLeft size={20} />
                        </Button>
                        <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-100 dark:border-gray-600 flex items-center justify-center shrink-0">
                            <DynamicIcon name={content.dashboard_id ? 'LayoutDashboard' : 'Box'} className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 m-0 leading-tight mb-1">
                                {content.name}
                            </h1>
                            <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                                <span title="Content Version">v{contentVersion}</span>
                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">Preview Mode</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        {/* Mock Run Button */}
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<LayoutDashboard size={18} />}
                            disabled={true}
                            title="Execution is disabled in Content Library preview."
                        >
                            Run Analysis
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content - Tabs & Views */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50 dark:bg-gray-900 animate-fadeIn">
                <div className="max-w-7xl mx-auto space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-0 overflow-hidden">
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabIndex} onChange={handleTabChange} aria-label="app tabs">
                                <Tab label="Insights" />
                                <Tab label="Execution History" />
                                <Tab label="About" />
                            </Tabs>
                        </Box>
                        <div className="p-4">
                            {tabIndex === 0 && (
                                <div className="bg-white dark:bg-gray-900 rounded-lg h-[600px] border border-gray-200 dark:border-gray-700 p-4">
                                    {content.dashboard_id ? (
                                        <div className="h-full">
                                            <DashboardViewPage
                                                dashboardId={content.dashboard_id}
                                                embedded={true}
                                                // Disable edit if published. If undefined, DashboardViewPage usually hides the edit button.
                                                onEdit={content.status === 'Published' ? undefined : () => window.open(`/dashboards/${content.dashboard_id}/edit`, '_blank')}
                                            />
                                        </div>
                                    ) : (
                                        <Box sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                            <BrainCircuit size={64} className="text-gray-300 mb-4" />
                                            <Typography variant="h6" color="textSecondary">No Default Dashboard</Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400 }}>
                                                This content does not have a dashboard assigned.
                                                In the Tenant App, a dynamic dashboard or raw results will be shown.
                                            </Typography>
                                        </Box>
                                    )}
                                </div>
                            )}
                            {tabIndex === 1 && (
                                <div className="p-12 text-center text-gray-500 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                                    <DynamicIcon name="History" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <h3 className="text-lg font-medium">Execution History Placeholder</h3>
                                    <p className="max-w-md mx-auto mt-2">
                                        This tab will show a history of all runs (Success, Failed, Queued) when deployed to a Tenant.
                                        In this preview, no actual execution data is available.
                                    </p>
                                </div>
                            )}
                            {tabIndex === 2 && (
                                <div className="p-6 prose dark:prose-invert max-w-none">
                                    <h3>{content.name}</h3>
                                    <p>{content.description || "No description provided."}</p>
                                    <hr className="my-4" />
                                    <h4 className="text-sm font-bold uppercase text-gray-500">Metadata</h4>
                                    <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded text-xs overflow-auto">
                                        {JSON.stringify({
                                            id: content.id,
                                            type: content.contentTypeName || 'ML Model',
                                            created: content.created_at,
                                            updated: content.updated_at,
                                            connections: content.connections?.map(c => c.name) || []
                                        }, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Box>
    );
};

export default MLAppRunnerPage;
